/** /mcp-connections — the signed-in ops user's own MCP connections (backend metaplan A7c; AIE
 *  plan §10 "MCP connections"), from the user menu.
 *
 *  Each row is an OAuth grant the authorization server holds for an MCP client this user signed
 *  in to (`/auth/oauth-handoff`): the client and how far its name can be trusted, the tenants it
 *  reaches and the MCP address it works at, the access it got, when it was connected and last
 *  refreshed, and the IP of that sign-in. **Revoke** ends the grant: its refresh tokens stop at
 *  once, an access token already issued within 15 minutes.
 *
 *  Tenant-bound MCP URLs (backend plan 2026-10-20, P5): an ops grant is on the all-tenant route
 *  (`ops`, `https://<ops host>/v1`: every tenant) or on one tenant's (`ops_tenant`,
 *  `…/v1/<code>`: its bound tenant, as it is now: `bound_tenant_code` / `bound_tenant_name`). A
 *  `stale` grant is badged: its address no longer names its binding, so the MCP server refuses it.
 *  "Your MCP addresses" is the list's `mcp_addresses` — the backend's configuration, so the page
 *  never builds a host itself — shown only while `mcp_eligible` (the user can connect now; MCP is
 *  not launched for everyone): otherwise "no MCP access yet" and no invitation to connect.
 *
 *  The user's own grants only (`tids=ops`) — the backend scopes both calls to the session's user,
 *  so the page needs no permission. Contract: `authserver.openapi.yaml` (tag `connections`). */
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Link as MuiLink,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import GppMaybeOutlinedIcon from '@mui/icons-material/GppMaybeOutlined';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';
import * as oauthApi from '@/api/oauth';
import type { OAuthConnection, OAuthMcpAddress } from '@/api/oauth';
import { visuallyHidden } from '@/components/BoolText';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { PageHeader } from '@/components/PageHeader';
import { CopyableId } from '@/components/ValueActions/ValueActions';
import { useSnackbar } from '@/components/Snackbar';
import { formatDate, formatRelativeTime } from '@/utils/format';
import { consoleRouteFor } from '@/utils/gridLinks';

export const CONNECTIONS_QUERY_KEY = ['oauth', 'connections'] as const;

const CLIENT_TYPE_LABEL: Record<OAuthConnection['client_type'], string> = {
  cimd: 'Identified by its domain',
  static: 'Registered by Curated Planet',
  dcr: 'Self-registered',
};

/** "Read" / "Read and write" for the contract's `mcp:read` / `mcp:write`; other scopes as sent. */
export function accessLabel(scopes: string[]): string {
  const known = scopes.filter((s) => s === 'mcp:read' || s === 'mcp:write');
  if (known.length === scopes.length && known.length > 0) {
    return scopes.includes('mcp:write') ? 'Read and write' : 'Read';
  }
  return scopes.length ? scopes.join(', ') : '—';
}

const MONO = { fontFamily: 'monospace', fontSize: 13, overflowWrap: 'anywhere' } as const;

const ADDRESS_LABEL: Record<OAuthMcpAddress['route'], string> = {
  ops: 'Every tenant',
  ops_tenant: 'One tenant',
  tenant: 'Tenant',
};

/** "Your MCP addresses": what to add to an AI client, as the API names them. */
function McpAddresses({ addresses }: { addresses: OAuthMcpAddress[] }) {
  const snackbar = useSnackbar();
  const headingId = useId();

  async function copy(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      snackbar.show('MCP address copied.', 'success');
    } catch {
      snackbar.show('Could not copy to clipboard.', 'error');
    }
  }

  return (
    <Paper component="section" variant="outlined" aria-labelledby={headingId} sx={{ p: 2, mb: 2 }}>
      <Typography id={headingId} component="h2" variant="subtitle2">
        Your MCP addresses
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Add one to your AI client as an MCP server, then sign in with this account.
      </Typography>
      <Stack spacing={1.5}>
        {addresses.map((a) => (
          <Stack key={`${a.route}:${a.url}`} direction="row" spacing={1} alignItems="center">
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" color="text.secondary" display="block">
                {a.route === 'tenant' && a.tenant_code ? a.tenant_code : ADDRESS_LABEL[a.route]}
              </Typography>
              <Typography sx={MONO}>{a.url}</Typography>
              {a.route === 'ops_tenant' && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {"Replace {tenant} with the tenant's code."}
                </Typography>
              )}
            </Box>
            <Tooltip title="Copy">
              <IconButton
                size="small"
                aria-label={`Copy ${a.url}`}
                onClick={() => void copy(a.url)}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
}

/** The tenants a grant reaches: every tenant on the all-tenant ops route, else its bound tenant
 *  as it is now (code linked to the tenant's page, name under it); `#<id>`, unlinked, for a
 *  tenant that is gone. */
function TenantCell({ connection: c }: { connection: OAuthConnection }) {
  if (c.route === 'ops') return <>Every tenant</>;
  const id = c.bound_tenant_id;
  const code = c.bound_tenant_code;
  if (!code) return <>{id !== null ? `#${id}` : '—'}</>;
  const href = id !== null ? consoleRouteFor('tour_operator', id) : null;
  return (
    <Stack spacing={0.25}>
      {href ? (
        <MuiLink component={RouterLink} to={href} underline="hover">
          {code}
        </MuiLink>
      ) : (
        <span>{code}</span>
      )}
      {c.bound_tenant_name && (
        <Typography variant="caption" color="text.secondary">
          {c.bound_tenant_name}
        </Typography>
      )}
    </Stack>
  );
}

function ClientCell({ connection: c }: { connection: OAuthConnection }) {
  return (
    <Stack spacing={0.25} sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
          {c.client_name || c.client_id}
        </Typography>
        {!c.verified && (
          <Tooltip
            describeChild
            title="This client registered itself: its name is its own claim, not checked by Curated Planet. Only the address under it is verified."
          >
            <Chip
              size="small"
              variant="outlined"
              color="warning"
              icon={<GppMaybeOutlinedIcon />}
              label="Unverified"
            />
          </Tooltip>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
        {c.redirect_host ? `${c.redirect_host} · ` : ''}
        {CLIENT_TYPE_LABEL[c.client_type] ?? c.client_type}
      </Typography>
    </Stack>
  );
}

export function McpConnectionsPage() {
  const qc = useQueryClient();
  const snackbar = useSnackbar();
  const [confirm, setConfirm] = useState<OAuthConnection | null>(null);

  const list = useQuery({ queryKey: CONNECTIONS_QUERY_KEY, queryFn: oauthApi.listConnections });

  const revoke = useMutation({
    mutationFn: (c: OAuthConnection) => oauthApi.revokeConnection(c.grant_id),
    onSuccess: (_data, c) => {
      snackbar.show(`Revoked ${c.client_name || c.client_id}.`, 'success');
    },
    onError: (e) => {
      if (oauthApi.oauthErrorCode(e) === 'connection_not_found') {
        snackbar.show('That connection had already ended.', 'info');
      } else {
        snackbar.show('Could not revoke the connection. Try again.', 'error');
      }
    },
    onSettled: () => qc.invalidateQueries({ queryKey: CONNECTIONS_QUERY_KEY }),
  });

  const connections = list.data?.connections ?? [];
  const addresses = list.data?.mcp_addresses ?? [];
  const eligible = list.data?.mcp_eligible;
  const disabled = oauthApi.oauthErrorCode(list.error) === 'authserver_disabled';

  return (
    <Box>
      <PageHeader
        title="MCP connections"
        subtitle="AI clients you have signed in to with this account, through MCP"
        helpStory="auth-mcp-connections"
      />

      {list.isPending && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {list.isError && disabled && (
        <Alert severity="info">
          MCP sign-in is not set up on this server, so there are no connections to show.
        </Alert>
      )}
      {list.isError && !disabled && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => void list.refetch()}>
              Retry
            </Button>
          }
        >
          {list.error instanceof ConsoleApiError && list.error.status === 502
            ? 'MCP connections are unavailable right now.'
            : 'Could not load your MCP connections.'}
        </Alert>
      )}

      {list.isSuccess && eligible === true && addresses.length > 0 && (
        <McpAddresses addresses={addresses} />
      )}
      {list.isSuccess && eligible === false && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Your account has no MCP access yet.
        </Typography>
      )}

      {list.isSuccess && connections.length === 0 && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="body2">No MCP clients are connected to your account.</Typography>
          {eligible === true && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              A client (Claude, Cursor, …) appears here once you sign in to it with this account.
            </Typography>
          )}
        </Paper>
      )}

      {list.isSuccess && connections.length > 0 && (
        <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="MCP connections">
            <TableHead>
              <TableRow>
                <TableCell>Client</TableCell>
                <TableCell>Tenant</TableCell>
                <TableCell>MCP address</TableCell>
                <TableCell>Access</TableCell>
                <TableCell>Connected</TableCell>
                <TableCell>Last used</TableCell>
                <TableCell>Sign-in IP</TableCell>
                <TableCell align="right">
                  <Box component="span" sx={visuallyHidden}>
                    Actions
                  </Box>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {connections.map((c) => (
                <TableRow key={c.grant_id}>
                  <TableCell sx={{ maxWidth: 360 }}>
                    <ClientCell connection={c} />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <TenantCell connection={c} />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 320, overflowWrap: 'anywhere' }}>
                    <CopyableId value={c.resource} label="Copy MCP address" />
                    {c.stale && (
                      <Stack spacing={0.25} sx={{ mt: 0.5, alignItems: 'flex-start' }}>
                        <Chip
                          size="small"
                          variant="outlined"
                          color="warning"
                          label="Address no longer valid"
                        />
                        <Typography variant="caption" color="text.secondary">
                          Revoke it and connect again.
                        </Typography>
                      </Stack>
                    )}
                  </TableCell>
                  <TableCell>{accessLabel(c.scopes)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(c.created_at)}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {c.last_used_at ? (
                      <Tooltip describeChild title={formatDate(c.last_used_at)}>
                        <span>{formatRelativeTime(c.last_used_at)}</span>
                      </Tooltip>
                    ) : (
                      'Not yet'
                    )}
                  </TableCell>
                  <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                    {c.login_ip ?? '—'}
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      color="error"
                      size="small"
                      disabled={revoke.isPending && revoke.variables?.grant_id === c.grant_id}
                      onClick={() => setConfirm(c)}
                      aria-label={`Revoke ${c.client_name || c.client_id}`}
                    >
                      Revoke
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <ConfirmDialog
        open={confirm !== null}
        title={`Revoke ${confirm?.client_name || confirm?.client_id || 'this connection'}?`}
        message="The client can no longer refresh its access. An access token it already holds stops working within 15 minutes. To use it again, connect it again from the client."
        confirmLabel="Revoke"
        destructive
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          if (confirm) revoke.mutate(confirm);
          setConfirm(null);
        }}
      />
    </Box>
  );
}
